import React, { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import * as portals from 'react-reverse-portal';

const StickyComponent = function ({
  offset = 0,
  className,
  children,
  wrapEl,
  stickyClassName,
  enabled = true,
}) {
  const wrapRef = useRef(null);
  const [isScrolledPast, setIsScrolledPast] = useState(false);
  const [minHeight, setMinHeight] = useState(0);

  const portalNode = useMemo(() => portals.createHtmlPortalNode(), []);

  const stickyTargetNode = typeof document !== 'undefined'
    ? document.getElementById(wrapEl)
    : null;

  const shouldRenderSticky = enabled && isScrolledPast && stickyTargetNode;

  const updateStickyStatus = (isPast) => {
    if (wrapRef.current) {
      setMinHeight(wrapRef.current.offsetHeight);
    }
    setIsScrolledPast(isPast);
  };

  useEffect(() => {
    if (!enabled) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        updateStickyStatus(!entry.isIntersecting);
      },
      {
        rootMargin: `-${offset}px 0px 0px 0px`,
        threshold: 1.0,
      }
    );

    const currentRef = wrapRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [offset, enabled]);

  return (
    <div className={className} ref={wrapRef} style={{ minHeight: shouldRenderSticky ? minHeight : 'auto' }}>
      <portals.InPortal node={portalNode}>
        {children}
      </portals.InPortal>

      {shouldRenderSticky ? (
        createPortal(
          <div className={stickyClassName}>
            <portals.OutPortal node={portalNode} />
          </div>,
          stickyTargetNode
        )
      ) : (
        <portals.OutPortal node={portalNode} />
      )}
    </div>
  );
};

StickyComponent.propTypes = {
  offset: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  wrapEl: PropTypes.string.isRequired,
  stickyClassName: PropTypes.string,
  enabled: PropTypes.bool,
};

export default StickyComponent;
